export const OPERATIONAL_HOUR_COUNTERS = [
  "RunningHours",
  "IgnitionHours",
  "PtoHours"
] as const;

export type OperationalHourCounter = (typeof OPERATIONAL_HOUR_COUNTERS)[number];

export interface OperationalHoursMetadataItem {
  field?: {
    uniqueId?: string;
  };
  value?: unknown;
}

export interface OperationalHoursCorrection {
  counter: OperationalHourCounter;
  previousDisplayed: number;
  newDisplayed: number;
  correctedAt: string | null;
  correctedBy: string | null;
}

export interface OperationalHoursRequestError {
  status: number;
  message: string;
}

const COUNTER_BY_VALUE: Record<string, OperationalHourCounter> = {
  RunningHours: "RunningHours",
  IgnitionHours: "IgnitionHours",
  PtoHours: "PtoHours",
  "1": "RunningHours",
  "2": "IgnitionHours",
  "3": "PtoHours"
};

const FORBIDDEN_MESSAGE = "You do not have permission to correct this counter.";
const NOT_FOUND_MESSAGE = "This object was not found for the selected account.";
const INVALID_MESSAGE = "The entered value is not valid.";
const GENERIC_MESSAGE =
  "Unable to correct operational hours. Please try again.";

export const readOperationalHoursValue = (
  metadata: OperationalHoursMetadataItem[] | null | undefined,
  metadataUniqueId: string
): number => {
  if (!metadata?.length) return 0;

  const match = metadata.find(
    (item) =>
      item?.field?.uniqueId?.toLowerCase() === metadataUniqueId.toLowerCase()
  );

  if (!match || match.value == null || match.value === "") return 0;

  const numeric =
    typeof match.value === "number"
      ? match.value
      : Number.parseFloat(String(match.value).trim().replace(",", "."));

  return Number.isFinite(numeric) ? numeric : 0;
};

export const formatOperationalHours = (value: number): string => {
  if (!Number.isFinite(value)) return "0,0";

  return value.toFixed(1).replace(".", ",");
};

export const isNoOpCorrection = (result: {
  correctedAt?: string | null;
  previousDisplayed: number;
  newDisplayed: number;
}): boolean => {
  const hasCorrectedAt =
    result.correctedAt != null && result.correctedAt !== "";

  return !hasCorrectedAt && result.newDisplayed === result.previousDisplayed;
};

const readResponseBody = (data: unknown): string => {
  if (typeof data === "string") return data.trim();
  if (!data || typeof data !== "object") return "";

  const record = data as Record<string, unknown>;

  if (typeof record.detail === "string" && record.detail.trim()) {
    return record.detail.trim();
  }

  if (record.errors && typeof record.errors === "object") {
    const messages = Object.values(record.errors as Record<string, unknown>)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0
      );

    if (messages.length > 0) return messages.join(" ");
  }

  if (typeof record.message === "string" && record.message.trim()) {
    return record.message.trim();
  }

  if (typeof record.error === "string" && record.error.trim()) {
    return record.error.trim();
  }

  if (typeof record.title === "string" && record.title.trim()) {
    return record.title.trim();
  }

  return "";
};

export const mapOperationalHoursError = (
  error: unknown
): OperationalHoursRequestError => {
  const apiError =
    typeof error === "object" && error !== null
      ? (error as {
          response?: { status?: number; data?: unknown };
          status?: number;
        })
      : {};

  const status = apiError.response?.status ?? apiError.status ?? 0;
  const bodyMessage = readResponseBody(apiError.response?.data);

  if (status === 409) {
    return { status, message: bodyMessage || GENERIC_MESSAGE };
  }

  if (status === 400) {
    return { status, message: bodyMessage || INVALID_MESSAGE };
  }

  if (status === 403) {
    return { status, message: FORBIDDEN_MESSAGE };
  }

  if (status === 404) {
    return { status, message: NOT_FOUND_MESSAGE };
  }

  return { status, message: bodyMessage || GENERIC_MESSAGE };
};

export const resolveCorrectedByName = (
  correctedBy: string | null | undefined,
  users:
    | Array<{
        uniqueId?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        user?: {
          uniqueId?: string;
          firstName?: string;
          lastName?: string;
          email?: string;
        };
      }>
    | null
    | undefined
): string => {
  if (!correctedBy) return "-";

  const wantedId = correctedBy.toLowerCase();
  const match = (users ?? []).find((entry) =>
    personIds(entry).some((id) => id.toLowerCase() === wantedId)
  );

  if (!match) return "-";

  const person =
    [match.user, match].find(
      (candidate) =>
        candidate &&
        (candidate.firstName || candidate.lastName || candidate.email)
    ) ?? match;
  const name = [person.firstName, person.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || person.email || "-";
};

const personIds = (entry: {
  uniqueId?: string;
  user?: { uniqueId?: string };
}): string[] =>
  [entry.uniqueId, entry.user?.uniqueId].filter(
    (id): id is string => typeof id === "string" && id.length > 0
  );

export const normalizeOperationalHoursCorrection = (
  raw: unknown
): OperationalHoursCorrection | null => {
  if (!raw || typeof raw !== "object") return null;

  const record = raw as Record<string, unknown>;
  const counter = COUNTER_BY_VALUE[String(record.counter)];

  if (!counter) return null;

  const previousDisplayed = Number(record.previousDisplayed);
  const newDisplayed = Number(record.newDisplayed);

  return {
    counter,
    previousDisplayed: Number.isFinite(previousDisplayed)
      ? previousDisplayed
      : 0,
    newDisplayed: Number.isFinite(newDisplayed) ? newDisplayed : 0,
    correctedAt:
      typeof record.correctedAt === "string" ? record.correctedAt : null,
    correctedBy:
      typeof record.correctedBy === "string" ? record.correctedBy : null
  };
};
